import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { Button } from "../components/index.js";
import type { DashboardSummary, LeadTimeSummary, PlanSummary } from "../types.js";
import { paginationInfo } from "../lib/shipments-view.js";
import { LeadTimeCard } from "./gerenciar/LeadTimeCard.js";
import { TotalShippedCard } from "./gerenciar/TotalShippedCard.js";
import {
  ShipmentsFilters,
  type ShipmentsFiltersValue
} from "./gerenciar/ShipmentsFilters.js";
import { ShipmentsTable } from "./gerenciar/ShipmentsTable.js";
import { PlanDetailModal } from "./gerenciar/PlanDetailModal.js";

/**
 * Gerenciar envios (Design.md §5): cards de Lead Time e Total enviado, barra de
 * filtros e tabela de remessas. Todos os dados são reais de /inbound-plans,
 * /dashboard e /lead-time (sem placeholders de exemplo).
 */
const PAGE_SIZE = 20;

const INITIAL_FILTERS: ShipmentsFiltersValue = {
  tab: "todas",
  status: "all",
  search: "",
  sortDesc: true
};

export function GerenciarEnviosPage(): ReactNode {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [leadTime, setLeadTime] = useState<LeadTimeSummary | null>(null);
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [filters, setFilters] = useState<ShipmentsFiltersValue>(INITIAL_FILTERS);
  const [offset, setOffset] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Sequência de requisição: ignora respostas obsoletas quando o usuário troca
  // status/página rápido (evita race de setState com payload antigo).
  const reqSeq = useRef(0);

  // Agregações dos cards: melhor esforço (falha não quebra a página).
  useEffect(() => {
    let cancelled = false;
    void api
      .getDashboard()
      .then((res) => {
        if (!cancelled) setDashboard(res.dashboard);
      })
      .catch(() => undefined);
    void api
      .getLeadTime()
      .then((res) => {
        if (!cancelled) setLeadTime(res.leadTime);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  const loadPlans = useCallback(async () => {
    const seq = ++reqSeq.current;
    setCarregando(true);
    setErro(null);
    try {
      const res = await api.listPlans({
        limit: PAGE_SIZE,
        offset,
        ...(filters.status !== "all" ? { status: filters.status } : {})
      });
      if (seq === reqSeq.current) {
        setPlans(res.plans);
      }
    } catch (error: unknown) {
      if (seq === reqSeq.current) {
        setErro(
          error instanceof Error
            ? error.message
            : "Falha ao carregar as remessas."
        );
      }
    } finally {
      if (seq === reqSeq.current) {
        setCarregando(false);
      }
    }
  }, [api, offset, filters.status]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  // Busca por ID e ordenação são client-side sobre a página atual (o backend não
  // tem param de busca; o sort por updatedAt não é parametrizável na lista).
  const visiblePlans = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const filtered =
      term.length === 0
        ? plans
        : plans.filter((p) => {
            const ref = p.spApiInboundPlanId ?? "";
            return (
              p.id.toLowerCase().includes(term) ||
              ref.toLowerCase().includes(term) ||
              (p.name ?? "").toLowerCase().includes(term)
            );
          });
    return [...filtered].sort((a, b) =>
      filters.sortDesc
        ? b.updatedAt.localeCompare(a.updatedAt)
        : a.updatedAt.localeCompare(b.updatedAt)
    );
  }, [plans, filters.search, filters.sortDesc]);

  const page = paginationInfo(offset, PAGE_SIZE, plans.length);

  function changeFilters(next: ShipmentsFiltersValue): void {
    // Mudar o filtro server-side (status) volta para a primeira página.
    if (next.status !== filters.status) {
      setOffset(0);
    }
    setFilters(next);
  }

  // (N) = total geral de planos (dashboard.totalPlans). Omitido quando há filtro
  // de status ativo, para não conflitar com a tabela filtrada; "(…)" enquanto
  // o dashboard carrega.
  const titleCount =
    filters.status !== "all"
      ? ""
      : dashboard !== null
        ? ` (${dashboard.totalPlans})`
        : " (…)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap"
        }}
      >
        <div>
          <h2 style={{ fontSize: 21, fontWeight: 700 }}>
            Gerenciar envios{titleCount}
          </h2>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
            Planos de inbound FBA · Amazon UK · Prep Center Bristol
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
          <Button variant="ghost" onClick={() => navigate("/criar")}>
            Enviar para a Amazon
          </Button>
          <Button onClick={() => void loadPlans()}>Atualizar lista</Button>
        </div>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "1.25rem"
        }}
      >
        <LeadTimeCard leadTime={leadTime} />
        <TotalShippedCard dashboard={dashboard} />
      </div>

      <ShipmentsFilters value={filters} onChange={changeFilters} />

      {erro !== null ? (
        <p role="alert" style={{ color: "var(--orange)" }}>
          {erro}
        </p>
      ) : null}

      {filters.tab === "rastreamento-ausente" ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          O filtro "Rastreamento ausente" depende do rastreamento por caixa (visível
          no detalhe da remessa) e ainda não é resolvido no nível da lista — exibindo
          todas as remessas.
        </p>
      ) : null}

      <ShipmentsTable
        plans={visiblePlans}
        loading={carregando}
        onView={(id) => setSelectedPlanId(id)}
        onResend={() => navigate("/criar")}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.75rem"
        }}
      >
        <Button
          variant="secondary"
          disabled={!page.canPrev || carregando}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          ‹ Anterior
        </Button>
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Página {page.page}
        </span>
        <Button
          variant="secondary"
          disabled={!page.canNext || carregando}
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
        >
          Próxima ›
        </Button>
      </div>

      <PlanDetailModal
        planId={selectedPlanId}
        onClose={() => setSelectedPlanId(null)}
      />
    </div>
  );
}
